import React from 'react'

import { Tooltip, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, shape, string, oneOfType, node, oneOf, bool, func } from 'prop-types'

import Icon from 'Pods/Shared/Icon/Icon'

function getThClassName(align, tooltip, sortable, isActive) {
  return classnames(
    `cgt-legacy-text--align-${align || 'left'}`,
    tooltip && 'has-tooltip',
    sortable && 'data-table__th--sortable',
    isActive && 'data-table__th--active'
  )
}

function getSortIcon(columnKey, sortKey, sortDirection) {
  if (columnKey !== sortKey) return 'sort'
  return sortDirection === 'asc' ? 'caret-up' : 'caret-down'
}

function getNextSortDirection(columnKey, sortKey, sortDirection) {
  if (columnKey !== sortKey) return 'asc'
  if (sortDirection === 'asc') return 'desc'
  return null
}

function TableHeader({
  columns,
  sortKey = null,
  sortDirection = null,
  onSort = null,
}) {
  const handleHeaderClick = (columnKey, sortable) => {
    if (!sortable || !onSort) return

    const nextDirection = getNextSortDirection(columnKey, sortKey, sortDirection)
    onSort(columnKey, nextDirection)
  }

  return (
    <thead>
      <tr>
        {columns.map(({
          key, title, tooltip, width, minWidth, align, sortable,
        }) => {
          const tooltipId = `tooltip-${key}`
          const isActive = sortable && key === sortKey
          const thClassName = getThClassName(align, tooltip, sortable, isActive)

          const renderContent = () => {
            const titleContent = tooltip ? (
              <Tooltip
                id={tooltipId}
                randomizeID
                content={tooltip}
                clickable
                place="top"
              >
                <span className="tooltip-trigger">{title}</span>
              </Tooltip>
            ) : (
              title
            )

            if (sortable) {
              return (
                <>
                  {titleContent}
                  <Icon
                    type={['fas', getSortIcon(key, sortKey, sortDirection)]}
                    className="data-table__sort-icon"
                    size="sm"
                  />
                </>
              )
            }

            return titleContent
          }

          return (
            <th
              key={key}
              className={thClassName}
              style={{
                ...(minWidth && { minWidth }),
                ...(width && { width }),
              }}
              onClick={() => handleHeaderClick(key, sortable)}
              role={sortable ? 'button' : undefined}
              tabIndex={sortable ? 0 : undefined}
              onKeyDown={sortable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleHeaderClick(key, sortable)
                }
              } : undefined}
            >
              {renderContent()}
            </th>
          )
        })}
      </tr>
    </thead>
  )
}

TableHeader.propTypes = {
  columns: arrayOf(
    shape({
      key: string.isRequired,
      title: oneOfType([string, node]).isRequired,
      tooltip: oneOfType([string, node]),
      width: string,
      align: oneOf(['left', 'center', 'right']),
      sortable: bool,
    })
  ).isRequired,
  sortKey: string,
  sortDirection: oneOf(['asc', 'desc']),
  onSort: func,
}

export { TableHeader }
